import api from "@/api";
import router from "@/router";
import i18n from "@/i18n";

const { t } = i18n.global;

export default {
  state: {
    customers: [],
    customersPerPage: {},
    currentCustomersPage: 0,
    customerDetails: {},
    customer: {},
    customerMembers: [],
  },
  getters: {
    customers: (state) => state.customers,
    customersPerPage: (state) => state.customersPerPage,
    currentCustomersPage: (state) => state.currentCustomersPage,
    customerDetails: (state) => state.customerDetails,
    customer: (state) => state.customer,
    customerMembers: (state) => state.customerMembers,
  },
  mutations: {
    setCustomers: (state, data) => {
      state.customers = data;
    },
    setCustomersPerPage: (state, data) => {
      state.customersPerPage = data;
    },
    setCurrentCustomersPage: (state, data) => {
      state.currentCustomersPage = data;
    },
    setCustomerDetails: (state, data) => {
      state.customerDetails = data;
    },
    setCustomer: (state, data) => {
      state.customer = data;
    },
    setCustomerMembers: (state, data) => {
      state.customerMembers = data;
    },
  },
  actions: {
    getCustomer: (_, customerId) =>
      new Promise((resolve, reject) => {
        api({ requiresAuth: true })
          .get(`/v1/customers/${customerId}`)
          .then(
            (response) => {
              const { data } = response.data;
              resolve(data);
            },
            (err) => {
              reject(err);
            }
          );
      }),

    getGlobalCustomer: async ({ commit, dispatch }, customerId) => {
      try {
        const response = await api({ requiresAuth: true }).get(
          `/v1/customers/${customerId}`
        );
        const { data } = response.data;
        commit("setCustomer", data);
        const { members, parent_id } = data;
        if (members.length) {
          commit("setCustomerMembers", [data, ...members]);
        } else if (parent_id && !members.length) {
          const response = await api({ requiresAuth: true }).get(
            `/v1/customers/${data.parent_id}`
          );
          const { data: customer } = response.data;
          const { members } = customer;
          commit("setCustomerMembers", [customer, ...members]);
        }
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.somethingWentWrong")
        });
      }
    },

    addCustomer: (
      { commit, dispatch },
      {
        companyName,
        companyType,
        language,
        customerAddresses,
        web,
        uid,
        customer_emails,
        customer_phones,
        name,
        account,
        address,
        member_type_id,
        gender,
        titleId,
        firstName,
        lastName,
        birthday,
        countryCode,
        ahvNr,
        occupation,
        employer,
        file,
        parentId,
        familyStatusId,
        teamId,
        customer_status_id,
        comment
      }
    ) =>
      new Promise((resolve, reject) => {
        commit("setCustomersLoading", true);
        const customer_addresses = Array.isArray(customerAddresses)
          ? customerAddresses.map((address) => {
              const mappedAddress = {
                id: address.id || "",
                number: address.number || "",
                street: address.street || "",
                zip: address.zip || "",
                city: address.city || "",
                post_box: address.postBox || address.post_box || "",
                type: address.type ?? address.addressType ?? null,
              };

              return mappedAddress;
            })
          : [
            (() => {
              const mappedAddress = {
                ...(customerAddresses?.id ? { id: customerAddresses.id } : {}),
                number: customerAddresses?.number || "",
                street: customerAddresses?.street || "",
                zip: customerAddresses?.zip || "",
                city: customerAddresses?.city || "",
                post_box: customerAddresses?.postBox || customerAddresses?.post_box || "",
                type: customerAddresses?.type ?? customerAddresses?.addressType ?? null,
              };

              return mappedAddress;
            })(),
          ];
        api({ requiresAuth: true })
          .post("/v1/customers", {
            company_name: companyName,
            company_type_id: companyType,
            language_id: language,
            customer_addresses,
            web,
            uuid: uid,
            customer_emails,
            customer_phones,
            member_type_id,
            gender_id: gender,
            title_id: titleId,
            first_name: firstName,
            last_name: lastName,
            birthday,
            country_code: countryCode,
            ahv_number: ahvNr,
            occupation,
            employer,
            parent_id: parentId,
            family_status_id: familyStatusId,
            team_id: teamId,
            customer_status_id,
            comment
          })
          .then(
            async (response) => {
              const { data } = response.data;

              if (name || account || address) {
                await dispatch("addCustomerBank", {
                  customerId: data.id,
                  name,
                  account,
                  address,
                });
              }

              if (file) {
                await dispatch("addCustomerPhoto", {
                  customerId: data.id,
                  file,
                });
              }

              dispatch("showToast", {
                type: "success",
                message: response.data.message || t("messages.customerCreatedSuccessfully")
              });
              resolve(data);
            },
            (err) => {
              dispatch("showToast", {
                type: "error",
                message: err.response?.data?.message || t("messages.somethingWentWrong")
              });
              reject(err);
            }
          );
        commit("setCustomersLoading", false);
      }),

    updateCustomer: (
      { commit, dispatch },
      {
        companyName,
        companyType,
        language,
        customerAddresses,
        web,
        uid,
        customer_emails,
        customer_phones,
        name,
        address,
        member_type_id,
        gender,
        titleId,
        firstName,
        lastName,
        birthday,
        countryCode,
        ahvNr,
        occupation,
        employer,
        account,
        companyId,
        agentId,
        bankId,
        file,
        teamId,
        customer_status_id,
        comment
      }
    ) =>
      new Promise((resolve, reject) => {
        const customer_addresses = Array.isArray(customerAddresses)
          ? customerAddresses.map((address) => {
              const mappedAddress = {
                ...(address.id ? { id: address.id } : {}),
                number: address.number || "",
                street: address.street || "",
                zip: address.zip || "",
                city: address.city || "",
                post_box: address.postBox || address.post_box || "",
                type: address.type ?? address.addressType ?? null,
              };

              return mappedAddress;
            })
          : [
            (() => {
              const mappedAddress = {
                ...(customerAddresses?.id ? { id: customerAddresses.id } : {}),
                number: customerAddresses?.number || "",
                street: customerAddresses?.street || "",
                zip: customerAddresses?.zip || "",
                city: customerAddresses?.city || "",
                post_box: customerAddresses?.postBox || customerAddresses?.post_box || "",
                type: customerAddresses?.type ?? customerAddresses?.addressType ?? null,
              };

              return mappedAddress;
            })(),
          ];
        api({ requiresAuth: true })
          .patch(`/v1/customers/${companyId}`, {
            company_name: companyName,
            company_type_id: companyType,
            language_id: language,
            customer_addresses,
            web,
            uuid: uid,
            customer_emails,
            customer_phones,
            member_type_id,
            gender_id: gender,
            title_id: titleId,
            first_name: firstName,
            last_name: lastName,
            birthday,
            country_code: countryCode,
            ahv_number: ahvNr,
            occupation,
            employer,
            team_id: teamId,
            agent_id: agentId,
            customer_status_id,
            comment
          })
          .then(
            async (response) => {
              const { data } = response.data;

              if (!bankId && (name || account)) {
                await dispatch("addCustomerBank", {
                  customerId: data.id,
                  name,
                  account,
                  address,
                });
              } else if (bankId) {
                await dispatch("updateCustomerBank", {
                  bankId,
                  customerId: data.id,
                  name,
                  account,
                  address,
                });
              }

              if (file) {
                await dispatch("addCustomerPhoto", {
                  customerId: data.id,
                  file,
                });
              }

              const { page } = router.currentRoute.value.query;
              commit("setCustomersLoading", true);
              if (page) {
                await dispatch("searchCustomers", { page });
              }
              dispatch("showToast", {
                type: "success",
                message: response.data.message || t("messages.customerUpdatedSuccessfully")
              });
              resolve(data);
              commit("setCustomersLoading", false);
            },
            (err) => {
              commit("setCustomersLoading", false);
              dispatch("showToast", {
                type: "error",
                message: err.response?.data?.message || t("messages.somethingWentWrong")
              });
              reject(err);
            }
          );
      }),

    deleteCustomer: async ({ commit, dispatch, getters }, customerId) => {
      commit("setCustomersLoading", true);
      try {
        await api({ requiresAuth: true }).delete(`/v1/customers/${customerId}`);
        await dispatch("searchCustomers", {
          page: getters.currentCustomersPage,
        });
        dispatch("showToast", {
          type: "success",
          message: t("messages.customerDeletedSuccessfully")
        });
        commit("setCustomersLoading", false);
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.somethingWentWrong")
        });
        commit("setCustomersLoading", false);
      }
    },
   separateMember: async ({ commit, dispatch }, customerId) => {
      commit("setCustomersLoading", true);
      try {
        await api({ requiresAuth: true }).post(`/v1/customers/${customerId}/separate`);
      /*  await dispatch("searchCustomers", {
          page: getters.currentCustomersPage,
        });*/
        dispatch("showToast", {
          type: "success",
          message: t("messages.memberSeparatedSuccessfully")
        });
        commit("setCustomersLoading", false);
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.somethingWentWrong")
        });
        commit("setCustomersLoading", false);
      }
    },
    addMember: async ( { commit, dispatch },{customerId,  members}) =>  new Promise((resolve, reject) => {
        commit("setCustomersLoading", true);

        api({ requiresAuth: true })
            .post(`/v1/customer-members/${customerId}/add-members`, {
            members: members
        }) .then(
            (response) => {
                const data = response.data.data;
                dispatch("showToast", {
                  type: "success",
                  message: response.data.message || t("messages.memberAddedSuccessfully")
                });
                commit("setCustomersLoading", false);
                resolve(data);
            },
            (err) => {
                dispatch("showToast", {
                  type: "error",
                  message: err.response?.data?.message || t("messages.somethingWentWrong")
                });
                commit("setCustomersLoading", false);
                reject(err);
            }
        );

         }),




    addCustomerPhoto: async ({ dispatch }, { customerId, file }) => {
      const formData = new FormData();
      formData.append("photo", file);
      try {
        await api({ requiresAuth: true }).post(
          `/v1/customers/${customerId}/files`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.somethingWentWrong")
        });
      }
    },

    deleteCustomerEmail: (_, emailId) => {
      return new Promise((resolve, reject) => {
        api({ requiresAuth: true })
          .delete(`/v1/customer-emails/${emailId}`)
          .then(
            (response) => {
              const { data } = response;
              resolve(data);
            },
            (err) => {
              reject(err);
            }
          );
      });
    },

    deleteCustomerPhone: (_, phoneId) => {
      return new Promise((resolve, reject) => {
        api({ requiresAuth: true })
          .delete(`/v1/customer-phones/${phoneId}`)
          .then(
            (response) => {
              const { data } = response;
              resolve(data);
            },
            (err) => {
              reject(err);
            }
          );
      });
    },
  },
};
